// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The VR arcade (M5-20, M5-21).
//
// "A late-night arcade: dark, carpeted in the loud geometric pattern arcades are legally required
// to have, brick and neon on the walls, a fogged window with rain, a change machine humming in
// the corner." (06 §4.) Every surface of it generated in code, because every asset in this
// product is (CLAUDE.md), and because a carpet pattern is one of the few things that is genuinely
// easier to write than to draw.
//
// **The venue exists so the machine is somewhere.** It is deliberately *dark* and deliberately
// *low detail away from the bay*: what a player looks at is one playfield, and every polygon
// spent on the far end of the room is a polygon taken from the thing they came for. The frame
// budget in a headset is 11.1 ms for two eyes (`tiers.ts`), and this has to fit inside what the
// machine leaves.
//
// **The neighbours are not machines.** A bay other than the player's gets a cabinet silhouette
// with a lit backglass panel that runs its own colour cycle — no playfield, no balls, no physics,
// no rules. From a metre away, through a headset, with your own machine's lights in your eyes,
// what reads as "the arcade is alive" is the *light*, and drawing five more complete machines to
// achieve it would cost five times the whole frame budget.

import {
  AdditiveBlending,
  BackSide,
  BoxGeometry,
  CanvasTexture,
  Color,
  Group,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  PlaneGeometry,
  RepeatWrapping,
  SRGBColorSpace,
  type BufferGeometry,
  type Material,
} from 'three'
import type { Bay } from '../xr/arcadeBays'

export interface ArcadeRoomBuild {
  readonly group: Group
  readonly geometries: readonly BufferGeometry[]
  readonly materials: readonly Material[]
  /** Generated canvases. Disposing a material does not dispose its map; the owner must. */
  readonly textures: readonly CanvasTexture[]
  /** Called once a frame with the elapsed time in seconds. Drives the attract cycles and the rain. */
  update: (seconds: number) => void
  /**
   * The player moved to another bay (M5-23), so a different cabinet is the real one.
   *
   * The silhouette at the player's bay is hidden rather than removed: the real machine stands in
   * exactly that spot, and two cabinets in one bay is the most visible possible artefact. Hiding
   * costs a boolean; rebuilding the venue on a teleport would cost every geometry in the room.
   */
  setPlayerBay: (index: number) => void
}

/** Room dimensions, metres. Wide enough for six bays plus somewhere to stand. */
export const ROOM = { width: 9.5, depth: 7.0, height: 3.2 } as const

/** The neon the walls carry, and the colours the neighbours' backglasses cycle through. */
const NEON_COLOURS = [0x3fe0ff, 0xff5fc8, 0x8b5cf6, 0x4ade80] as const

/**
 * Build the venue around a row of bays.
 *
 * `playerBay` is excluded from the neighbour cabinets, because the player's own machine is the
 * real one and is built by `MachineScene`.
 */
export function buildArcadeRoom(bays: readonly Bay[], playerBay: number): ArcadeRoomBuild {
  const group = new Group()
  const geometries: BufferGeometry[] = []
  const materials: Material[] = []
  const track = <T extends BufferGeometry>(g: T): T => {
    geometries.push(g)
    return g
  }
  const trackMaterial = <T extends Material>(m: T): T => {
    materials.push(m)
    return m
  }

  // --- the shell ------------------------------------------------------------------
  //
  // One inverted box rather than six planes: a box drawn from the inside is one draw call and
  // cannot develop a seam at a corner, and the room is never seen from outside.
  const shell = track(new BoxGeometry(ROOM.width, ROOM.height, ROOM.depth))
  const brick = trackMaterial(
    new MeshStandardMaterial({
      color: 0x241d29,
      roughness: 0.95,
      metalness: 0,
      side: BackSide,
    }),
  )
  const walls = new Mesh(shell, brick)
  walls.position.y = ROOM.height / 2
  group.add(walls)

  // --- the carpet -----------------------------------------------------------------
  const carpetTexture = carpet()
  const carpetMaterial = trackMaterial(
    new MeshStandardMaterial({
      ...(carpetTexture === undefined ? { color: 0x1b1440 } : { map: carpetTexture }),
      roughness: 1,
      metalness: 0,
    }),
  )
  const floor = new Mesh(track(new PlaneGeometry(ROOM.width, ROOM.depth)), carpetMaterial)
  floor.rotation.x = -Math.PI / 2
  floor.position.y = 0.001
  group.add(floor)

  // --- neon along the side walls ---------------------------------------------------
  for (let i = 0; i < 4; i++) {
    const colour = NEON_COLOURS[i % NEON_COLOURS.length] as number
    const material = trackMaterial(
      new MeshBasicMaterial({ color: new Color(colour), toneMapped: false }),
    )
    const tube = new Mesh(track(new BoxGeometry(0.06, 0.06, ROOM.depth * 0.7)), material)
    tube.position.set(
      i % 2 === 0 ? -ROOM.width / 2 + 0.09 : ROOM.width / 2 - 0.09,
      i < 2 ? 2.5 : 1.9,
      0,
    )
    group.add(tube)
  }

  // --- the window, with rain --------------------------------------------------------
  //
  // A single additive plane with a scrolling generated streak texture. Not a shader: a scrolling
  // texture offset costs one uniform write a frame and a custom shader costs a program the tier
  // budget has not accounted for, and at the distance and brightness this is seen from, nobody
  // has ever been able to tell.
  const rainTexture = rain()
  const windowMaterial = trackMaterial(
    new MeshBasicMaterial({
      ...(rainTexture === undefined ? { color: 0x2a3550 } : { map: rainTexture }),
      transparent: true,
      opacity: 0.5,
      blending: AdditiveBlending,
      toneMapped: false,
      depthWrite: false,
    }),
  )
  const fogged = new Mesh(track(new PlaneGeometry(1.6, 1.1)), windowMaterial)
  fogged.position.set(ROOM.width / 2 - 0.02, 1.7, 1.4)
  fogged.rotation.y = -Math.PI / 2
  group.add(fogged)

  // --- the change machine ------------------------------------------------------------
  const changeBody = trackMaterial(
    new MeshStandardMaterial({ color: 0x2b2f3a, roughness: 0.5, metalness: 0.6 }),
  )
  const change = new Mesh(track(new BoxGeometry(0.5, 1.4, 0.35)), changeBody)
  change.position.set(-ROOM.width / 2 + 0.45, 0.7, ROOM.depth / 2 - 0.5)
  group.add(change)
  const changeGlow = trackMaterial(new MeshBasicMaterial({ color: 0xffc857, toneMapped: false }))
  const changePanel = new Mesh(track(new PlaneGeometry(0.34, 0.12)), changeGlow)
  changePanel.position.set(change.position.x, 1.15, change.position.z + 0.18)
  group.add(changePanel)

  // --- the neighbours ------------------------------------------------------------------
  const attractPanels: {
    mesh: Mesh
    material: MeshBasicMaterial
    base: Color
    phase: number
  }[] = []
  const silhouette = trackMaterial(
    new MeshStandardMaterial({ color: 0x15121c, roughness: 0.8, metalness: 0.1 }),
  )
  const bodyGeometry = track(new BoxGeometry(0.724, 0.95, 1.295))
  const headGeometry = track(new BoxGeometry(0.724, 0.72, 0.16))
  const glassGeometry = track(new PlaneGeometry(0.62, 0.58))

  const silhouettes = new Map<number, Group>()
  for (const bay of bays) {
    const cabinet = new Group()
    cabinet.position.set(bay.x, 0, bay.z)
    cabinet.rotation.y = bay.yaw

    const body = new Mesh(bodyGeometry, silhouette)
    body.position.y = 0.55
    cabinet.add(body)

    const head = new Mesh(headGeometry, silhouette)
    head.position.set(0, 1.42, -0.56)
    cabinet.add(head)

    const colour = NEON_COLOURS[bay.index % NEON_COLOURS.length] as number
    const glassMaterial = trackMaterial(
      new MeshBasicMaterial({ color: new Color(colour), toneMapped: false }),
    )
    const glass = new Mesh(glassGeometry, glassMaterial)
    glass.position.set(0, 1.42, -0.47)
    cabinet.add(glass)
    // A phase per bay, so six machines do not flash in unison — which is the single thing that
    // would make the room read as one animation rather than as six machines.
    attractPanels.push({
      mesh: glass,
      material: glassMaterial,
      base: new Color(colour),
      phase: bay.index * 1.31,
    })

    cabinet.visible = bay.index !== playerBay
    silhouettes.set(bay.index, cabinet)
    group.add(cabinet)
  }

  const setPlayerBay = (index: number): void => {
    for (const [bay, cabinet] of silhouettes) cabinet.visible = bay !== index
  }

  const update = (seconds: number): void => {
    for (const panel of attractPanels) {
      // A slow breath with a fast flicker under it — an attract mode is a light show, and a
      // pure sine reads as a heartbeat monitor.
      const breath = 0.55 + 0.45 * Math.sin(seconds * 1.7 + panel.phase)
      const flicker = 0.9 + 0.1 * Math.sin(seconds * 11.3 + panel.phase * 3)
      // From the bay's own colour every frame, never from the last frame's — scaling the live
      // colour compounds, and six backglasses would fade to black over about a minute.
      panel.material.color.copy(panel.base).multiplyScalar(breath * flicker)
      panel.mesh.visible = breath > 0.12
    }
    if (rainTexture !== undefined) {
      // Rain runs down. The horizontal drift is what stops it reading as a barcode.
      rainTexture.offset.y = -((seconds * 0.35) % 1)
      rainTexture.offset.x = Math.sin(seconds * 0.21) * 0.01
    }
  }

  // Textures separately from materials, because disposing a material does **not** dispose its
  // map — Three leaves that to the owner, and the owner is whoever created it. Two generated
  // canvases leaked per venue rebuild would be invisible until a player re-placed the machine
  // twenty times.
  const textures = [carpetTexture, rainTexture].filter((t): t is CanvasTexture => t !== undefined)
  return { group, geometries, materials, textures, update, setPlayerBay }
}

/** The document, or undefined — the same guard `artTexture.ts` uses, and for the same reason. */
function canvasHost(): { createElement: (tag: string) => HTMLCanvasElement } | undefined {
  return (globalThis as { document?: { createElement: (tag: string) => HTMLCanvasElement } })
    .document
}

/**
 * The carpet.
 *
 * Every arcade carpet ever printed is the same idea: a very dark ground, a scatter of bright
 * geometric confetti, and one or two swooping shapes. It is drawn once at 256², tiles across the
 * floor, and never changes — so it costs one texture upload for the life of the session.
 *
 * Deterministic by construction: the pattern comes from an integer walk rather than
 * `Math.random`, so the venue looks the same every time a player enters it. A carpet that
 * reshuffled itself on every session would be the sort of detail nobody could name and everybody
 * would feel.
 */
function carpet(): CanvasTexture | undefined {
  const host = canvasHost()
  if (host === undefined) return undefined
  const size = 256
  const canvas = host.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const ctx = canvas.getContext('2d')
  if (ctx === null) return undefined

  ctx.fillStyle = '#120c22'
  ctx.fillRect(0, 0, size, size)

  // A cheap integer hash walk: reproducible, no PRNG dependency, and good enough for confetti.
  let seed = 0x9e3779b9
  const next = (): number => {
    seed = (seed * 1664525 + 1013904223) >>> 0
    return seed / 0x100000000
  }
  const colours = ['#3fe0ff', '#ff5fc8', '#8b5cf6', '#4ade80', '#ffc857']
  for (let i = 0; i < 90; i++) {
    ctx.save()
    ctx.translate(next() * size, next() * size)
    ctx.rotate(next() * Math.PI)
    ctx.fillStyle = colours[Math.floor(next() * colours.length)] as string
    ctx.globalAlpha = 0.55 + next() * 0.35
    const w = 4 + next() * 16
    const h = 3 + next() * 6
    ctx.fillRect(-w / 2, -h / 2, w, h)
    ctx.restore()
  }
  // Two swoops, because a real one always has them.
  ctx.globalAlpha = 0.3
  for (let i = 0; i < 2; i++) {
    ctx.strokeStyle = colours[i] as string
    ctx.lineWidth = 9
    ctx.beginPath()
    ctx.moveTo(-10, 60 + i * 110)
    ctx.bezierCurveTo(70, 10 + i * 120, 190, 150 + i * 60, size + 10, 40 + i * 150)
    ctx.stroke()
  }

  const texture = new CanvasTexture(canvas)
  texture.colorSpace = SRGBColorSpace
  texture.wrapS = RepeatWrapping
  texture.wrapT = RepeatWrapping
  texture.repeat.set(ROOM.width / 1.4, ROOM.depth / 1.4)
  return texture
}

/** Rain streaks on glass, scrolled by `update`. */
function rain(): CanvasTexture | undefined {
  const host = canvasHost()
  if (host === undefined) return undefined
  const w = 128
  const h = 256
  const canvas = host.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d')
  if (ctx === null) return undefined

  ctx.fillStyle = '#0a1120'
  ctx.fillRect(0, 0, w, h)
  let seed = 0x1f123bb5
  const next = (): number => {
    seed = (seed * 1103515245 + 12345) >>> 0
    return seed / 0x100000000
  }
  ctx.strokeStyle = 'rgba(190, 220, 255, 0.55)'
  for (let i = 0; i < 70; i++) {
    const x = next() * w
    const y = next() * h
    const length = 6 + next() * 26
    ctx.lineWidth = next() < 0.2 ? 1.6 : 0.8
    ctx.beginPath()
    ctx.moveTo(x, y)
    ctx.lineTo(x + 1.5, y + length)
    ctx.stroke()
  }

  const texture = new CanvasTexture(canvas)
  texture.colorSpace = SRGBColorSpace
  texture.wrapS = RepeatWrapping
  texture.wrapT = RepeatWrapping
  return texture
}
